import {
  formatDateTime,
  formatIdr,
  formatUsd,
  type Topup,
  type TopupStatus,
} from '@ytclip/shared'
import { ExternalLink, QrCode, Receipt, RefreshCw } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import QRCode from 'react-qr-code'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Badge, type BadgeTone, EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader, Pagination, TableScroller, Td, Th } from '@/components/ui/misc.tsx'
import { fetchTopupStatus } from '@/features/billing/api.ts'
import { openPaymentPage } from '@/features/billing/payment-page.ts'
import { useTransactions } from '@/features/shared-hooks.ts'

const STATUS_META: Record<TopupStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Menunggu', tone: 'warning' },
  paid: { label: 'Lunas', tone: 'success' },
  expired: { label: 'Kedaluwarsa', tone: 'neutral' },
  failed: { label: 'Gagal', tone: 'error' },
  cancelled: { label: 'Dibatalkan', tone: 'neutral' },
}

export function TransactionsPage() {
  const [page, setPage] = useState(1)
  const [qrTopup, setQrTopup] = useState<Topup | null>(null)

  const { data, isPending, isError } = useTransactions({ page, pageSize: 10 })

  return (
    <>
      <PageHeader title="Transaksi" description="Riwayat top-up saldo" />

      <Card>
        <CardBody>
          {isPending ? (
            <LoadingBlock />
          ) : isError ? (
            <ErrorState message="Riwayat transaksi tidak bisa dimuat." />
          ) : data.data.length === 0 ? (
            <EmptyState
              icon={<Receipt />}
              title="Belum ada transaksi"
              description="Top-up pertamamu akan muncul di sini."
            />
          ) : (
            <>
              <TableScroller>
                <table className="w-full min-w-2xl border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant">
                      <Th>Tanggal</Th>
                      <Th>Kredit</Th>
                      <Th className="text-right">Total bayar</Th>
                      <Th>Status</Th>
                      <Th><span className="sr-only">Aksi</span></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((topup) => (
                      <tr
                        key={topup.id}
                        className="border-b border-outline-variant/50 transition-colors hover:bg-surface-container-low"
                      >
                        <Td className="whitespace-nowrap text-label-md">
                          {formatDateTime(topup.createdAt)}
                        </Td>
                        <Td className="font-medium">{formatUsd(topup.usdAmount)}</Td>
                        <Td className="text-right tabular-nums">{formatIdr(topup.totalIdr)}</Td>
                        <Td>
                          <Badge tone={STATUS_META[topup.status].tone}>
                            {STATUS_META[topup.status].label}
                          </Badge>
                        </Td>
                        <Td className="text-right">
                          {topup.status === 'pending' ? (
                            <div className="flex justify-end gap-1">
                              <CheckPaymentButton topupId={topup.id} />
                              {topup.qrString ? (
                                <Button
                                  variant="text"
                                  size="sm"
                                  onClick={() => setQrTopup(topup)}
                                  icon={<QrCode className="size-4" />}
                                >
                                  Lihat QR
                                </Button>
                              ) : topup.paymentUrl ? (
                                <Button
                                  variant="text"
                                  size="sm"
                                  onClick={() => openPaymentPage(topup.paymentUrl)}
                                  icon={<ExternalLink className="size-4" />}
                                >
                                  Bayar
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>

              <Pagination
                page={data.pagination.page}
                totalPages={data.pagination.totalPages}
                total={data.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={Boolean(qrTopup)}
        onOpenChange={() => setQrTopup(null)}
        title="Kode pembayaran"
        description="Scan dengan aplikasi bank atau e-wallet kamu."
      >
        {qrTopup?.qrString ? (
          <div className="space-y-4">
            <div className="mx-auto w-fit rounded-md bg-surface-container-lowest p-5 shadow-level-1">
              <QRCode value={qrTopup.qrString} size={200} bgColor="#FFFFFF" fgColor="#1C1B1F" />
            </div>
            <p className="text-center text-headline-md font-medium text-on-surface">
              {formatIdr(qrTopup.totalIdr)}
            </p>
            <p className="text-center text-body-md text-on-surface-variant">
              untuk {formatUsd(qrTopup.usdAmount)} kredit
            </p>
            {qrTopup.paymentUrl ? (
              <Button
                variant="text"
                className="w-full"
                onClick={() => openPaymentPage(qrTopup.paymentUrl)}
                icon={<ExternalLink className="size-4" />}
              >
                Kode tidak bisa discan? Buka halaman pembayaran
              </Button>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </>
  )
}

/**
 * Forces an immediate check against the payment gateway for one transaction.
 *
 * The background sweep already reconciles pending payments within a minute,
 * but someone who has just paid and is looking at this page wants an answer
 * now — and waiting without feedback is what makes a working system feel
 * broken.
 */
function CheckPaymentButton({ topupId }: { topupId: string }) {
  const queryClient = useQueryClient()

  const check = useMutation({
    mutationFn: () => fetchTopupStatus(topupId, true),
    onSuccess: (topup) => {
      if (topup.status === 'paid') {
        toast.success(`Pembayaran diterima. Saldo bertambah ${formatUsd(topup.usdAmount)}.`)
        void queryClient.invalidateQueries({ queryKey: ['balance'] })
        void queryClient.invalidateQueries({ queryKey: ['membership'] })
      } else if (topup.status === 'pending') {
        toast.info('Belum ada pembayaran yang tercatat. Coba lagi beberapa saat lagi.')
      } else {
        toast.error('Kode pembayaran ini sudah tidak berlaku.')
      }

      void queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    onError: () => toast.error('Gagal memeriksa status pembayaran'),
  })

  return (
    <Button
      variant="text"
      size="sm"
      loading={check.isPending}
      onClick={() => check.mutate()}
      icon={<RefreshCw className="size-4" />}
    >
      Saya sudah bayar
    </Button>
  )
}
